import { Component, OnInit, OnDestroy } from "@angular/core";
import { FormBuilder, Validators, FormGroup, FormArray } from "@angular/forms";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { Subject, Subscription } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ToastrService } from "ngx-toastr";
import * as moment from "moment";
import { EventInput } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import bootstrapPlugin from "@fullcalendar/bootstrap";

import { DataService } from "src/app/shared/Services/data.service";
import { GetCurrentShift } from "src/app/shared/Services/getCurrentShift";

@Component({
  selector: "app-production-planning",
  templateUrl: "./production-planning.component.html",
  styleUrls: ["./production-planning.component.scss"],
})
export class ProductionPlanningComponent implements OnInit, OnDestroy {
  // --- UI & Breadcrumbs ---
  breadCrumbItems = [
    { label: "Configuration" },
    { label: "Production Planning", active: true },
  ];
  settings = {};
  isbtnEnable = false;
  isReset = false;
  isEditMode = false;
  color: any;

  // --- Calendar Configurations ---
  calendarPlugins = [
    dayGridPlugin,
    timeGridPlugin,
    interactionPlugin,
    listPlugin,
    bootstrapPlugin,
  ];
  calendarEvents: EventInput[] = [];
  calendarWeekends: any;

  // --- Date & Time State ---
  currentDate: string;
  currentMoment!: moment.Moment;
  today: string;
  minDate: string;
  maxDate: string;
  getdate: any;
  editDate: any;
  fromDate: any;
  toDate: any;

  // --- Shift Management State ---
  isCurrentShift = false;
  isShiftEditable = false;
  shiftName: string;
  usershiftId: string;
  currentShiftDate: string;
  currentShiftData: any;
  shiftStartMoment!: moment.Moment;
  shiftEndMoment!: moment.Moment;

  // --- Master Data Arrays ---
  departmentData: any[] = [];
  deviceData: any[] = [];
  devicelistData: any[] = [];
  usersData: any[] = [];
  usersSupervisor: any[] = [];
  shiftsArr: any[] = [];
  productPlanArr: any[] = [];
  partData: any[] = [];
  operationPart: any = [];
  partoperationPart: any = [];

  // --- Current Selections ---
  selecteddepartmentName: string;
  selecteddepartmentId: string;
  selectedDeviceName: string;
  selectedId: any;
  deviceId: string;
  deviceType: string;
  custId: string;
  userData: any;

  // --- Reactive Forms & RxJS ---
  formData: FormGroup;
  isEnable: boolean;
  partOperationMap: { [index: number]: any[] } = {};
  private subscriptions: Subscription[] = [];
  private destroy$ = new Subject<void>();

  // --- edit form ---
  step = 1;
  changeOnlyOperator: boolean | null = null;
  configureProduction: boolean | null = null;
  oldOperatorName!: string;
  selectedOperatorIndex!: number;
  selectedOperatorName!: string;

  constructor(
    private modalService: NgbModal,
    private formBuilder: FormBuilder,
    private dataservice: DataService,
    private toastr: ToastrService,
    private currShift: GetCurrentShift,
  ) {}

  // ==========================================
  // LIFECYCLE HOOKS
  // ==========================================

  ngOnInit() {
    this.initializeUIConfig();
    this.initializeDateBounds();
    this.loadUserData();
    this.loadInitialData();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  private initializeUIConfig() {
    this.settings = {
      singleSelection: false,
      idField: "item_id",
      textField: "item_text",
      selectAllText: "Select All",
      unSelectAllText: "Unselect All",
      itemsShowLimit: 2,
      allowSearchFilter: true,
    };
  }

  private initializeDateBounds() {
    this.currentDate = moment().format("YYYY-MM-DD");
    this.today = moment().format("YYYY-MM-DD");
    this.minDate = moment().subtract(7, "days").format("YYYY-MM-DD");
    this.maxDate = moment().add(7, "days").format("YYYY-MM-DD");
  }

  private loadUserData() {
    this.userData = JSON.parse(sessionStorage.getItem("currentUser"));
    this.custId = this.userData?.user?.custId;
  }

  private loadInitialData() {
    this.loadCycleTime(this.custId);
    this.loadDepartments();
  }

  // ==========================================
  // DATA FETCHING (API)
  // ==========================================

  loadCycleTime(custId: string) {
    this.dataservice
      .get(`programName/masterList/${custId}`)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        if (res?.result?.length) this.partData = res.result;
      });
  }

  loadDepartments() {
    this.dataservice
      .get("department")
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        if (res?.data?.length) {
          this.departmentData = res.data;
          this.selectDepartment(this.departmentData[0]);
          this.loadDevices();
        }
      });
  }

  loadDevices() {
    this.dataservice
      .get("api/device")
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res: any) => {
          if (res?.length) {
            this.deviceData = res;
            this.devicelistData = this.deviceData.filter(
              (d) =>
                d.departmentName === this.selecteddepartmentName &&
                d.partcountType !== "edge",
            );
            if (this.devicelistData.length) {
              this.deviceId = this.devicelistData[0].deviceId;
              this.selectedDeviceName = this.devicelistData[0].deviceName;
              this.deviceType = this.devicelistData[0].oee;
              this.loadUsers();
              this.getProductPlanning();
            }
          }
        },
        () => this.toastr.error("Server Unreachable", "Error"),
      );
  }

  loadUsers() {
    this.dataservice
      .get("api/user")
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res: any) => {
          this.usersData = res.filter((u) => u.role === "OPERATOR");
          this.usersSupervisor = res.filter((u) => u.role === "SUPERVISOR");
        },
        () => this.toastr.error("Server Unreachable", "Error"),
      );
  }

  getProductPlanning() {
    this.dataservice
      .get(
        `mapping/programMapping?custId=${this.custId}&deviceId=${this.deviceId}`,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res: any) => {
          this.calendarEvents = [];
          this.productPlanArr = res;
          this.productPlanArr.forEach((elm) => {
            if (elm.events?.length) this.calendarEvents.push(...elm.events);
          });
        },
        () => this.toastr.error("Server Unreachable", "Error"),
      );
  }

  // ==========================================
  // EVENT HANDLERS
  // ==========================================

  departmentChange(el: string) {
    const department = this.departmentData.find((d) => d.departmentName === el);
    if (!department) return;
    this.selectDepartment(department);
    this.devicelistData = this.deviceData.filter(
      (d) =>
        d.departmentName === this.selecteddepartmentName &&
        d.partcountType !== "edge",
    );
    if (this.devicelistData.length) {
      this.deviceId = this.devicelistData[0].deviceId;
      this.selectedDeviceName = this.devicelistData[0].deviceName;
      this.deviceType = this.devicelistData[0].oee;
      this.getProductPlanning();
    }
  }

  deviceChange(el: string) {
    const data = this.deviceData.find((d) => d.deviceName === el);
    if (data) {
      this.deviceId = data.deviceId;
      this.selectedDeviceName = data.deviceName;
      this.deviceType = data.oee;
      this.getProductPlanning();
    }
  }

  shiftChange(shiftName: string) {
    const selectedShift = this.shiftsArr.find((s) => s.shiftName === shiftName);
    if (!selectedShift || !this.getdate) return;

    this.isEnable = this.funDate(this.getdate, selectedShift.usershiftId);
    if (this.isEnable) {
      this.toastr.info("Selected Shift is completed", "Info");
      return;
    }

    this.shiftName = selectedShift.shiftName;
    this.usershiftId = selectedShift.usershiftId;

    if (this.formData?.contains("usershiftId")) {
      this.formData.patchValue({
        usershiftId: this.usershiftId,
        shiftName: this.shiftName,
      });
    }

    const range = this.getShiftDateRange(this.getdate, selectedShift);
    this.fromDate = range.fromDate;
    this.toDate = range.toDate;
    this.shiftStartMoment = moment(this.fromDate);
    this.shiftEndMoment = moment(this.toDate);
    this.currentMoment = moment();

    this.isCurrentShift =
      this.getdate === this.currentShiftDate &&
      this.currentMoment.isBetween(
        this.shiftStartMoment,
        this.shiftEndMoment,
        undefined,
        "[)",
      );

    this.clearTargetArray();
    if (this.deviceType !== "no_part") {
      this.addPartwiseTarget("add");
    }

    const planExists = this.checkPlanExists(this.getdate, shiftName);
    if (planExists) {
      this.toastr.warning("Already Plan Exist", "Warning");
      this.isEnable = true;
    }
  }

  // ==========================================
  // FORM & PLANNING LOGIC
  // ==========================================

  get targetFormArray(): FormArray {
    return this.formData.get("target") as FormArray;
  }

  initForm() {
    if (this.deviceType !== "no_part") {
      this.formData = this.formBuilder.group({
        date: [this.getdate, Validators.required],
        custId: [this.custId, Validators.required],
        departmentName: [this.selecteddepartmentName, Validators.required],
        departmentId: [this.selecteddepartmentId, Validators.required],
        deviceName: [this.selectedDeviceName, Validators.required],
        deviceId: [this.deviceId, Validators.required],
        usershiftId: [this.currentShiftData.usershiftId, Validators.required],
        shiftName: [this.currentShiftData.shiftName, Validators.required],
        target: this.formBuilder.array([]),
        events: [[]],
        isSamePartNextShift: [false],
        oeeType: "part",
      });
    } else {
      this.formData = this.formBuilder.group({
        date: [this.getdate, Validators.required],
        custId: [this.custId, Validators.required],
        departmentName: [this.selecteddepartmentName, Validators.required],
        departmentId: [this.selecteddepartmentId, Validators.required],
        deviceName: [this.selectedDeviceName, Validators.required],
        deviceId: [this.deviceId, Validators.required],
        usershiftId: [this.currentShiftData.usershiftId, Validators.required],
        shiftName: [this.currentShiftData.shiftName, Validators.required],
        operatorName: ["", Validators.required],
        target: this.formBuilder.array([]),
        events: [[]],
        isSamePartNextShift: [false],
        oeeType: "nopart",
      });
    }
  }

  onPartChange(event: any, index: number) {
    const selectedPartName = event.target.value;
    const row = this.targetFormArray.at(index);
    row.get("operationName")?.reset("");
    row.get("target")?.reset("");
    row.get("shiftCapacity")?.reset("");

    const selectedPart = this.partData.find(
      (p) => p.partName === selectedPartName,
    );
    this.partOperationMap[index] = [];

    if (selectedPart?.operationData?.length) {
      selectedPart.operationData.forEach((op: any, i: number) => {
        this.partOperationMap[index][i] = {
          operationId: op.operationId,
          operatorName: op.name,
          partName: selectedPart.partName,
          item_text: `${selectedPart.partName}-${op.operationName}`,
          isCycleTime: true,
          runningTime: Number(op.runningTime) || 0,
          loadingTime: Number(op.loadingTime) || 0,
          unloadingTime: Number(op.unloadingTime) || 0,
          sequenceData: op.sequenceData || [],
          countingType: selectedPart.countingType,
          partsPerCycle: selectedPart.partsPerCycle,
        };
      });
    }
  }

  onOperationChange(event: any, index: number) {
    const rowval = this.targetFormArray.at(index);
    rowval.get("target")?.reset("");
    rowval.get("shiftCapacity")?.reset("");

    const opText = event.target.value;
    const operation = this.partOperationMap[index].find(
      (o) => o.item_text === opText,
    );
    if (!operation) return;

    const cycleTimeSec =
      operation.runningTime + operation.loadingTime + operation.unloadingTime;
    this.calculateCapacity(index, cycleTimeSec);

    const row = this.targetFormArray.at(index);
    row.patchValue({
      runningTime: operation.runningTime,
      loadingTime: operation.loadingTime,
      unloadingTime: operation.unloadingTime,
      countingType: operation.countingType,
      partsPerCycle: operation.partsPerCycle,
      sequenceData: operation.sequenceData || [],
    });
  }

  captureOldOperator(index: number) {
    const row = this.targetFormArray.at(index) as FormGroup;
    this.oldOperatorName = row.get("operatorName")?.value;
  }

  onOperatorChange(content: any, event: any, index: number, act: string) {
    if (act === "add") {
      (this.targetFormArray.at(index) as FormGroup).patchValue({
        operatorName: event.target.value,
      });
      return;
    }

    // Edit mode
    if (
      this.isCurrentShift &&
      !this.isFuturePacketByIndex(index) &&
      !this.isReset
    ) {
      this.selectedOperatorIndex = index;
      this.selectedOperatorName = event.target.value;

      // Revert select UI to old value while modal is shown
      event.target.value = this.oldOperatorName;

      this.modalService.open(content, { backdrop: "static", centered: true });
    } else {
      // Future packet or reset mode — patch directly
      (this.targetFormArray.at(index) as FormGroup).patchValue({
        operatorName: event.target.value,
      });
    }
  }

  NoonOperatorChange(event: any, act: string) {
    const name = event.target.value;
    const targetArray = this.targetFormArray;

    while (targetArray.length !== 0) {
      targetArray.removeAt(0);
    }

    let currentTime: string;
    const now = moment();
    const startOfHour = now.clone().startOf("hour");
    const fromMoment = moment(this.fromDate);

    if (act === "add") {
      if (this.isCurrentShift) {
        currentTime = startOfHour.isBefore(fromMoment)
          ? fromMoment.format("YYYY-MM-DD HH:mm:ss")
          : startOfHour.format("YYYY-MM-DD HH:mm:ss");
      } else {
        currentTime = fromMoment.format("YYYY-MM-DD HH:mm:ss");
      }
    } else {
      currentTime = this.isCurrentShift
        ? now.format("YYYY-MM-DD HH:mm:ss")
        : fromMoment.format("YYYY-MM-DD HH:mm:ss");
    }

    const maxToDate =
      act === "add"
        ? this.getMaxShiftEnd().format("YYYY-MM-DD HH:mm:ss")
        : this.toDate;

    targetArray.push(
      this.formBuilder.group({
        operatorName: [name, Validators.required],
        startTime: [currentTime],
        endTime: [maxToDate],
      }),
    );
  }

  isCurrentPacketByIndex(index: number): boolean {
    const row = this.targetFormArray.at(index) as FormGroup;
    if (!row) return false;
    const value = row.getRawValue();
    const now = moment();
    return (
      now.isSameOrAfter(moment(value.startTime)) &&
      now.isBefore(moment(value.endTime))
    );
  }

  isFuturePacketByIndex(index: number): boolean {
    const row = this.targetFormArray.at(index) as FormGroup;
    if (!row) return false;
    return moment().isBefore(moment(row.getRawValue().startTime));
  }

  checkPlanExists(date: string, shiftName: string): boolean {
    return this.productPlanArr.some(
      (item) =>
        item.date === date &&
        item.deviceName === this.selectedDeviceName &&
        (shiftName === "All Shift" ||
          shiftName === "all_shift" ||
          item.shiftName === "All Shift" ||
          item.shiftName === "all_shift" ||
          item.shiftName === shiftName),
    );
  }

  getMaxShiftEnd(): moment.Moment {
    if (!this.isEditMode) {
      const selectedShift = this.shiftsArr.find(
        (s) => s.shiftName === this.shiftName,
      );
      if (selectedShift && this.getdate && this.maxDate) {
        const adjustedMaxDate = moment(this.maxDate, "YYYY-MM-DD")
          .clone()
          .subtract(1, "day");
        const range = this.getShiftDateRange(
          adjustedMaxDate.format("YYYY-MM-DD"),
          selectedShift,
        );
        return moment(range.toDate);
      }
    }
    return moment(this.toDate);
  }

  getSingleShiftAvailableSeconds(startTime: moment.Moment): number {
    const shiftEnd = moment(this.toDate);
    return shiftEnd.diff(startTime, "seconds");
  }

  getAvailableSeconds(startTime: moment.Moment): number {
    if (this.isEditMode) {
      const shiftEnd = moment(this.toDate);
      return shiftEnd.diff(startTime, "seconds");
    }

    const selectedShift = this.shiftsArr.find(
      (s) => s.shiftName === this.shiftName,
    );
    if (!selectedShift || !this.getdate || !this.maxDate) {
      const shiftEnd = moment(this.toDate);
      return shiftEnd.diff(startTime, "seconds");
    }

    const adjustedMaxDate = moment(this.maxDate, "YYYY-MM-DD")
      .clone()
      .subtract(1, "day");

    let totalSeconds = 0;
    let currentStart = startTime.clone();
    let currentDate = currentStart.format("YYYY-MM-DD");

    for (let day = 0; day < 30; day++) {
      if (moment(currentDate).isAfter(adjustedMaxDate)) {
        break;
      }

      const range = this.getShiftDateRange(currentDate, selectedShift);
      const shiftStart = moment(range.fromDate);
      const shiftEnd = moment(range.toDate);

      let startOfProductionOnDay = currentStart.isBefore(shiftStart)
        ? shiftStart
        : currentStart;

      if (startOfProductionOnDay.isBefore(shiftEnd)) {
        const availableSecondsOnDay = shiftEnd.diff(
          startOfProductionOnDay,
          "seconds",
        );
        totalSeconds += availableSecondsOnDay;
      }

      const nextDate = moment(currentDate, "YYYY-MM-DD")
        .add(1, "day")
        .format("YYYY-MM-DD");
      currentDate = nextDate;
      const nextRange = this.getShiftDateRange(nextDate, selectedShift);
      currentStart = moment(nextRange.fromDate);
    }

    return totalSeconds;
  }

  getCalculatedEndTime(
    startTime: moment.Moment,
    target: number,
    cycleTimeSec: number,
  ): moment.Moment {
    if (this.isEditMode) {
      return startTime.clone().add(target * cycleTimeSec, "seconds");
    }

    const selectedShift = this.shiftsArr.find(
      (s) => s.shiftName === this.shiftName,
    );
    if (!selectedShift) {
      return startTime.clone().add(target * cycleTimeSec, "seconds");
    }

    let remainingSeconds = target * cycleTimeSec;
    let currentStart = startTime.clone();
    let currentDate = currentStart.format("YYYY-MM-DD");

    for (let day = 0; day < 30; day++) {
      const range = this.getShiftDateRange(currentDate, selectedShift);
      const shiftStart = moment(range.fromDate);
      const shiftEnd = moment(range.toDate);

      let startOfProductionOnDay = currentStart.isBefore(shiftStart)
        ? shiftStart
        : currentStart;

      if (startOfProductionOnDay.isBefore(shiftEnd)) {
        const availableSecondsOnDay = shiftEnd.diff(
          startOfProductionOnDay,
          "seconds",
        );
        if (remainingSeconds <= availableSecondsOnDay) {
          return startOfProductionOnDay
            .clone()
            .add(remainingSeconds, "seconds");
        }
        remainingSeconds -= availableSecondsOnDay;
      }

      const nextDate = moment(currentDate, "YYYY-MM-DD")
        .add(1, "day")
        .format("YYYY-MM-DD");
      currentDate = nextDate;
      const nextRange = this.getShiftDateRange(nextDate, selectedShift);
      currentStart = moment(nextRange.fromDate);
    }

    return startTime.clone().add(target * cycleTimeSec, "seconds");
  }

  calculateCapacity(index: number, cycleTimeSec: number) {
    console.log(this.toDate, "this.toDate");
    const row = this.targetFormArray.at(index);
    const startTime = moment(row.get("startTime")?.value);
    const availableSeconds = this.isEditMode
      ? this.getAvailableSeconds(startTime)
      : this.getSingleShiftAvailableSeconds(startTime);
    const capacity =
      availableSeconds <= 0 || cycleTimeSec <= 0
        ? 0
        : Math.floor(availableSeconds / cycleTimeSec);

    console.log(availableSeconds, startTime, cycleTimeSec, capacity);
    // Temporarily enable to allow setValue on disabled control
    const ctrl = row.get("shiftCapacity");
    const wasDisabled = ctrl?.disabled;
    ctrl?.enable({ emitEvent: false });
    ctrl?.setValue(capacity);
    if (wasDisabled) ctrl?.disable({ emitEvent: false });
  }

  onTargetChange(index: number) {
    const row = this.targetFormArray.at(index);
    const target = Number(row.get("target")?.value);
    if (!target || target <= 0) return;

    const opText = row.get("operationName")?.value;
    const operation = this.partOperationMap[index]?.find(
      (o) => o.item_text === opText,
    );
    if (!operation) return;

    const cycleTimeSec =
      operation.runningTime + operation.loadingTime + operation.unloadingTime;
    const startTime = moment(row.get("startTime")?.value);
    const maxShiftEnd = this.getMaxShiftEnd();
    const calculatedEndTime = this.getCalculatedEndTime(
      startTime,
      target,
      cycleTimeSec,
    );

    if (calculatedEndTime.isAfter(maxShiftEnd)) {
      const dayShiftEndTimeStr = maxShiftEnd.format("YYYY-MM-DD HH:mm:ss");

      const availableSeconds = this.getAvailableSeconds(startTime);
      const capacity =
        availableSeconds <= 0 || cycleTimeSec <= 0
          ? 0
          : Math.floor(availableSeconds / cycleTimeSec);

      this.toastr.error(
        `Entered target exceeds the permitted production duration and machine capacity. Maximum achievable capacity is ${capacity} units, with an estimated end time of ${dayShiftEndTimeStr}.`,
      );
      row.get("target")?.reset();
      row.get("endTime")?.reset();
      return;
    }

    row
      .get("endTime")
      ?.setValue(calculatedEndTime.format("YYYY-MM-DD HH:mm:ss"));

    const nextRow = this.targetFormArray.at(index + 1);
    if (nextRow) {
      nextRow
        .get("startTime")
        ?.setValue(
          calculatedEndTime
            .clone()
            .add(1, "minute")
            .format("YYYY-MM-DD HH:mm:ss"),
        );
    }
  }

  addPartwiseTarget(val: any) {
    if (val === "edit") {
      this.isReset = true;
    }

    const targetArray = this.targetFormArray;

    if (targetArray.length > 0) {
      const last = targetArray.at(targetArray.length - 1) as FormGroup;
      if (last.invalid) {
        this.toastr.warning(
          "Please complete Target and End Time before adding next part",
          "Validation",
        );
        last.markAllAsTouched();
        return;
      }
    }

    // Disable all existing rows including startTime select
    targetArray.controls.forEach((ctrl) => {
      ctrl.disable({ emitEvent: false });
      (ctrl as FormGroup).get("startTime")?.disable({ emitEvent: false });
    });

    // Determine startMoment for the new row
    let startMoment: moment.Moment;

    if (targetArray.length === 0) {
      // First row
      startMoment = this.isCurrentShift
        ? moment()
        : this.shiftStartMoment.clone();
    } else {
      const prevEndVal = targetArray
        .at(targetArray.length - 1)
        .get("endTime")?.value;
      const prevEnd = moment(prevEndVal).add(1, "minute");

      if (val === "edit" && this.isCurrentShift) {
        // Current shift edit: use prevEnd or now, whichever is later
        startMoment = prevEnd.isAfter(moment()) ? prevEnd : moment();
      } else {
        startMoment = prevEnd;
      }
    }

    targetArray.push(
      this.formBuilder.group({
        partName: ["", Validators.required],
        operationName: ["", Validators.required],
        operatorName: ["", Validators.required],
        shiftCapacity: [""],
        target: ["", Validators.required],
        startTime: [startMoment.format("YYYY-MM-DD HH:mm:ss")],
        endTime: [""],
        runningTime: [0],
        loadingTime: [0],
        unloadingTime: [0],
        partsPerCycle: [0],
        countingType: [""],
        sequenceData: [[]],
      }),
    );

    // Auto-select first time option for the new row
    const newIndex = targetArray.length - 1;
    const options = this.getTimeOptions(newIndex);
    if (options.length) {
      const autoStart = moment(options[0], "YYYY-MM-DD HH:mm");
      targetArray
        .at(newIndex)
        .get("startTime")
        ?.setValue(autoStart.format("YYYY-MM-DD HH:mm:ss"));
    }
  }

  removeTargetRow(index: number) {
    this.targetFormArray.removeAt(index);
    this.targetFormArray.controls.forEach((ctrl, i) => {
      if (i === this.targetFormArray.length - 1)
        ctrl.enable({ emitEvent: false });
      else ctrl.disable({ emitEvent: false });
    });
  }

  removeTargetEditRow(index: number) {
    this.targetFormArray.removeAt(index);

    this.targetFormArray.controls.forEach((ctrl, i) => {
      const group = ctrl as FormGroup;
      const isLast = i === this.targetFormArray.length - 1;
      const isCurrent = this.isCurrentPacketByIndex(i);

      group.disable({ emitEvent: false });

      if (isLast && !isCurrent) {
        group.enable({ emitEvent: false });
      }
      if (isLast && isCurrent) {
        group.get("operatorName")?.enable({ emitEvent: false });
      }
    });
  }

  // ==========================================
  // TIME OPTIONS & SELECTION
  // ==========================================

  getTimeOptions(index: number): string[] {
    const options: string[] = [];
    const selectedShift = this.shiftsArr.find(
      (s) => s.shiftName === this.shiftName,
    );
    if (!selectedShift) return options;

    let cursor: moment.Moment;

    if (this.isEditMode) {
      // Edit mode: use saved startTime as cursor start for existing rows
      const savedStart = this.targetFormArray
        .at(index)
        ?.get("startTime")?.value;
      if (savedStart) {
        cursor = moment(savedStart).seconds(0).milliseconds(0);
      } else if (index > 0) {
        const prevEnd = this.targetFormArray
          .at(index - 1)
          .get("endTime")?.value;
        cursor = prevEnd
          ? moment(prevEnd).add(1, "minute").seconds(0).milliseconds(0)
          : this.isCurrentShift
            ? moment().seconds(0).milliseconds(0)
            : moment(this.fromDate).seconds(0).milliseconds(0);
      } else {
        cursor = this.isCurrentShift
          ? moment().seconds(0).milliseconds(0)
          : moment(this.fromDate).seconds(0).milliseconds(0);
      }
    } else {
      // Add mode
      cursor = this.isCurrentShift
        ? moment().seconds(0).milliseconds(0)
        : moment(this.fromDate).seconds(0).milliseconds(0);

      if (index > 0) {
        const prevEnd = this.targetFormArray
          .at(index - 1)
          .get("endTime")?.value;
        if (prevEnd) {
          cursor = moment(prevEnd).add(1, "minute").seconds(0).milliseconds(0);
        }
      }
    }

    let currentDateStr = cursor.format("YYYY-MM-DD");
    let range = this.getShiftDateRange(currentDateStr, selectedShift);
    let shiftEnd = moment(range.toDate);

    if (cursor.isSameOrAfter(shiftEnd)) {
      const nextDateStr = cursor.clone().add(1, "day").format("YYYY-MM-DD");
      range = this.getShiftDateRange(nextDateStr, selectedShift);
      cursor = moment(range.fromDate);
      shiftEnd = moment(range.toDate);
    }

    while (cursor.isBefore(shiftEnd)) {
      options.push(cursor.format("YYYY-MM-DD HH:mm"));
      cursor.add(1, "minute");
    }

    const currentVal = this.getStartTimeHHMM(index);
    if (currentVal && !options.includes(currentVal)) {
      options.unshift(currentVal);
    }

    return options;
  }

  onStartTimeSelect(event: any, index: number) {
    const selected = event.target.value; // "YYYY-MM-DD HH:mm"
    const row = this.targetFormArray.at(index);
    const newStart = moment(selected, "YYYY-MM-DD HH:mm");

    row.get("startTime")?.setValue(newStart.format("YYYY-MM-DD HH:mm:ss"));
    row.get("endTime")?.reset("");
    row.get("target")?.reset("");

    const opText = row.get("operationName")?.value;
    const operation = this.partOperationMap[index]?.find(
      (o) => o.item_text === opText,
    );

    const capacityCtrl = row.get("shiftCapacity");
    const wasDisabled = capacityCtrl?.disabled;
    capacityCtrl?.enable({ emitEvent: false });

    if (operation) {
      const cycleTimeSec =
        Number(operation.runningTime || 0) +
        Number(operation.loadingTime || 0) +
        Number(operation.unloadingTime || 0);

      const availableSeconds = this.isEditMode
        ? this.getAvailableSeconds(newStart)
        : this.getSingleShiftAvailableSeconds(newStart);

      capacityCtrl?.setValue(
        availableSeconds > 0 && cycleTimeSec > 0
          ? Math.floor(availableSeconds / cycleTimeSec)
          : 0,
      );
    } else {
      capacityCtrl?.reset("");
    }

    if (wasDisabled) capacityCtrl?.disable({ emitEvent: false });
  }

  getStartTimeHHMM(index: number): string {
    const val = this.targetFormArray.at(index)?.get("startTime")?.value;
    if (!val) return "";
    return moment(val).format("YYYY-MM-DD HH:mm");
  }

  // ==========================================
  // MODAL CONTROLS
  // ==========================================

  openModal(content: any, event?: any) {
    this.isEditMode = false;
    this.getdate = event.dateStr ? event.dateStr : this.editDate;
    const diffDays = moment(this.currentDate)
      .startOf("day")
      .diff(moment(this.getdate).startOf("day"), "days");

    if (diffDays <= 0 || diffDays === 1) {
      const selectedShift = this.shiftsArr.find(
        (s) => s.shiftName === this.currentShiftData.shiftName,
      );
      if (!selectedShift) return;

      if (this.funDate(this.getdate, selectedShift.usershiftId)) {
        this.toastr.info("Selected Date is already completed", "Info");
        return;
      }

      const planExists = this.checkPlanExists(
        this.getdate,
        selectedShift.shiftName,
      );
      if (planExists) {
        this.toastr.warning("Already Plan Exist", "Warning");
        this.isEnable = true;
      } else {
        this.isEnable = false;
      }

      const range = this.getShiftDateRange(this.getdate, selectedShift);
      this.fromDate = range.fromDate;
      this.toDate = range.toDate;
      this.shiftStartMoment = moment(this.fromDate);
      this.shiftEndMoment = moment(this.toDate);
      this.currentMoment = moment();

      this.isCurrentShift =
        this.getdate === this.currentShiftDate &&
        this.currentMoment.isBetween(
          this.shiftStartMoment,
          this.shiftEndMoment,
          undefined,
          "[)",
        );

      this.initForm();
      if (this.deviceType !== "no_part") {
        this.addPartwiseTarget("add");
      }
      this.modalService.open(content, { backdrop: "static", centered: true });
    }
  }

  openEditModal(editcontent: any, arg: any) {
    this.isEditMode = true;
    this.initForm();

    const event = arg.event;

    const fullPlanning = this.productPlanArr.find(
      (item) =>
        item.date === event.extendedProps?.eventDate &&
        item.shiftName === event.extendedProps?.shiftName &&
        item.deviceName === event.extendedProps?.deviceName,
    );

    if (!fullPlanning) {
      this.toastr.error("Planning not found", "Error");
      return;
    }

    this.editDate = fullPlanning.date;
    this.selectedId = fullPlanning.id;

    // Handle deleted shift
    let selectedShift = this.shiftsArr.find(
      (s) => s.shiftName === fullPlanning.shiftName,
    );
    if (!selectedShift) {
      selectedShift = {
        shiftName: fullPlanning.shiftName,
        fromTime: fullPlanning.fromTime,
        toTime: fullPlanning.toTime,
        isDeleted: true,
      };
      this.shiftsArr.push(selectedShift);
    }

    // Shift time calculation
    const range = this.getShiftDateRange(fullPlanning.date, selectedShift);
    this.shiftStartMoment = moment(range.fromDate);
    this.shiftEndMoment = moment(range.toDate);
    this.fromDate = range.fromDate;
    this.toDate = range.toDate;
    this.currentMoment = moment();

    this.isCurrentShift =
      moment(fullPlanning.date).isSame(moment(), "day") &&
      this.currentMoment.isBetween(
        this.shiftStartMoment,
        this.shiftEndMoment,
        undefined,
        "[)",
      );

    this.isShiftEditable = !this.funDate(
      fullPlanning.date,
      fullPlanning.usershiftId,
    );

    // Patch form header
    if (this.deviceType !== "no_part") {
      this.formData.patchValue({
        date: fullPlanning.date,
        custId: fullPlanning.custId,
        departmentName: fullPlanning.departmentName,
        departmentId: fullPlanning.departmentId,
        deviceName: fullPlanning.deviceName,
        deviceId: fullPlanning.deviceId,
        shiftName: fullPlanning.shiftName,
        usershiftId: fullPlanning.usershiftId,
        events: fullPlanning.events || [],
        isSamePartNextShift: fullPlanning.isSamePartNextShift || false,
        oeeType: fullPlanning.oeeType || "part",
      });
    } else {
      this.formData.patchValue({
        date: fullPlanning.date,
        custId: fullPlanning.custId,
        departmentName: fullPlanning.departmentName,
        operatorName: fullPlanning.operatorName,
        departmentId: fullPlanning.departmentId,
        deviceName: fullPlanning.deviceName,
        deviceId: fullPlanning.deviceId,
        shiftName: fullPlanning.shiftName,
        usershiftId: fullPlanning.usershiftId,
        events: fullPlanning.events || [],
        isSamePartNextShift: fullPlanning.isSamePartNextShift || false,
        oeeType: fullPlanning.oeeType || "nopart",
      });
    }

    this.formData.get("shiftName")?.disable();
    this.formData.get("deviceName")?.disable();
    this.clearTargetArray();

    // Build target rows
    if (this.deviceType !== "no_part" && fullPlanning.target?.length) {
      fullPlanning.target.forEach((row, i) => {
        this.mapOperationsForEdit(row, i);

        const operatorExists = this.usersData.some(
          (u) => u.name === row.operatorName,
        );
        if (!operatorExists && row.operatorName) {
          this.usersData.push({ name: row.operatorName, isDeleted: true });
        }

        const group = this.formBuilder.group({
          partName: [row.partName, Validators.required],
          operatorName: [row.operatorName, Validators.required],
          operationName: [row.operationName, Validators.required],
          shiftCapacity: [row.shiftCapacity],
          runningTime: [row.runningTime],
          loadingTime: [row.loadingTime],
          unloadingTime: [row.unloadingTime],
          sequenceData: [row?.sequenceData || []],
          target: [row.target, Validators.required],
          startTime: [row.startTime], // always use saved startTime
          endTime: [row.endTime],
          partsPerCycle: [row.partsPerCycle || 0],
          countingType: [row.countingType || ""],
        });

        group.disable();

        const now = Date.now();
        const startMs = new Date(row.startTime).getTime();
        const endMs = new Date(row.endTime).getTime();
        const isCurrent = now >= startMs && now < endMs;
        const isFuture = now < startMs;
        if (isCurrent || isFuture) {
          group.get("operatorName")?.enable();
        }

        this.targetFormArray.push(group);
      });

      // Last row full enable only if shift is NOT current
      const lastRow = this.targetFormArray.at(this.targetFormArray.length - 1);
      if (!this.isCurrentShift && lastRow) {
        lastRow.enable();
      }
    }

    this.modalService.open(editcontent, { backdrop: "static", centered: true });
  }

  private mapOperationsForEdit(row: any, i: number) {
    let selectedPart = this.partData.find((p) => p.partName === row.partName);

    if (!selectedPart) {
      selectedPart = {
        partName: row.partName,
        operationData: [],
        isDeleted: true,
      };
      this.partData.push(selectedPart);
    }

    this.partOperationMap[i] = [];

    if (selectedPart?.operationData?.length) {
      selectedPart.operationData.forEach((op: any, idx: number) => {
        this.partOperationMap[i][idx] = {
          operationId: op.operationId,
          partName: selectedPart.partName,
          item_text: `${selectedPart.partName}-${op.operationName}`,
          runningTime: Number(op.runningTime) || 0,
          loadingTime: Number(op.loadingTime) || 0,
          unloadingTime: Number(op.unloadingTime) || 0,
          sequenceData: op.sequenceData || [],
        };
      });
    }

    const operationExists = this.partOperationMap[i].some(
      (op) => op.item_text === row.operationName,
    );
    if (!operationExists && row.operationName) {
      this.partOperationMap[i].push({
        item_text: row.operationName,
        isDeleted: true,
      });
    }
  }

  // ==========================================
  // SAVE & DELETE ACTIONS
  // ==========================================

  addEventData() {
    if (this.formData.invalid) {
      this.toastr.info("Please fill all the fields", "Info");
      return;
    }

    const formValue = this.formData.getRawValue();
    const selectedShift = this.shiftsArr.find(
      (s) => s.shiftName === formValue.shiftName,
    );
    if (!selectedShift) return;

    const shiftStart = moment(
      `${formValue.date} ${selectedShift.startTime}`,
      "YYYY-MM-DD HH:mm:ss",
    ).toISOString();
    const shiftEnd = moment(
      `${formValue.date} ${selectedShift.endTime}`,
      "YYYY-MM-DD HH:mm:ss",
    ).toISOString();

    const event = {
      title: `${this.selectedDeviceName}_${formValue.shiftName}`,
      start: shiftStart,
      end: shiftEnd,
      backgroundColor: this.getRandomColor(),
      eventDate: formValue.date,
      shiftName: formValue.shiftName,
      deviceName: formValue.deviceName,
    };

    this.formData.patchValue({ events: [event] });
    this.saveEvent("Add");
  }

  saveEvent(data: string) {
    if (this.formData.invalid) {
      this.toastr.info("Please fill all the fields", "Info");
      return;
    }

    const payload = {
      ...this.formData.getRawValue(),
      target: this.targetFormArray.getRawValue(),
    };

    const action =
      data === "Add"
        ? this.dataservice.post(`mapping/programMapping`, payload)
        : this.dataservice.put(
            `mapping/programMapping/${this.selectedId}`,
            payload,
          );

    action.subscribe({
      next: (res: any) => {
        this.isReset = false;
        this.toastr.success(
          res?.message || `Planning ${data}ed Successfully`,
          "Success",
        );
        this.getProductPlanning();
        this.close();
      },
      error: (err) => this.handleSaveError(err),
    });
  }

  deletePlanning() {
    if (!this.selectedId) return;
    if (this.isCurrentShift || !this.isShiftEditable) {
      this.toastr.warning(
        "Cannot delete current or completed shift",
        "Warning",
      );
      return;
    }

    this.dataservice
      .delete(`mapping/programMapping/${this.selectedId}`)
      .subscribe({
        next: (res: any) => {
          this.toastr.success(
            res?.message || "Planning deleted successfully",
            "Success",
          );
          this.getProductPlanning();
          this.close();
        },
        error: (err) => this.toastr.error(err || "Server Error", "Error"),
      });
  }

  // ==========================================
  // HELPERS & UTILS
  // ==========================================

  private selectDepartment(department: any) {
    this.selecteddepartmentName = department.departmentName;
    this.selecteddepartmentId = department.departmentId;
    const rawShifts = department.shiftData || [];
    this.currentShiftData = this.currShift.getCurrentShift(
      rawShifts,
      this.userData.user.timeZone,
    );
    this.shiftsArr = [...rawShifts];
    if (
      this.shiftsArr.length &&
      !this.shiftsArr.some((s) => s.shiftName === "All Shift")
    ) {
      const dummyDate = "2026-01-01";
      let earliestShift = this.shiftsArr[0];
      let latestShift = this.shiftsArr[0];
      let earliestFrom = moment(
        this.getShiftDateRange(dummyDate, earliestShift).fromDate,
      );
      let latestTo = moment(
        this.getShiftDateRange(dummyDate, latestShift).toDate,
      );

      for (let i = 1; i < this.shiftsArr.length; i++) {
        const s = this.shiftsArr[i];
        const range = this.getShiftDateRange(dummyDate, s);
        const fromVal = moment(range.fromDate);
        const toVal = moment(range.toDate);

        if (fromVal.isBefore(earliestFrom)) {
          earliestFrom = fromVal;
          earliestShift = s;
        }
        if (toVal.isAfter(latestTo)) {
          latestTo = toVal;
          latestShift = s;
        }
      }

      this.shiftsArr.push({
        shiftName: "All Shift",
        usershiftId: "all_shift",
        startTime: earliestShift.startTime,
        endTime: latestShift.endTime,
        startNextDayFlag: earliestShift.startNextDayFlag,
        endNextDayFlag: latestShift.endNextDayFlag,
      });
    }
    this.shiftName = this.currentShiftData.shiftName;
    this.usershiftId = this.currentShiftData.usershiftId;
    this.currentShiftDate = moment(this.currentShiftData.currentDate).format(
      "YYYY-MM-DD",
    );
  }

  getShiftDateRange(baseDate: string, shift: any) {
    let startBase = moment(baseDate, "YYYY-MM-DD");
    if (shift.startNextDayFlag) startBase = startBase.add(1, "day");
    const fromDate = moment(
      `${startBase.format("YYYY-MM-DD")} ${shift.startTime}`,
      "YYYY-MM-DD HH:mm:ss",
    );

    let endBase = startBase.clone();
    if (shift.endNextDayFlag) endBase = endBase.add(1, "day");
    const toDate = moment(
      `${endBase.format("YYYY-MM-DD")} ${shift.endTime}`,
      "YYYY-MM-DD HH:mm:ss",
    );

    return {
      fromDate: fromDate.format("YYYY-MM-DD HH:mm:ss"),
      toDate: toDate.format("YYYY-MM-DD HH:mm:ss"),
    };
  }

  funDate(date, shift) {
    if (shift === "all_shift" || shift === "All Shift") {
      if (moment(date).isBefore(this.currentShiftDate, "day")) {
        return true;
      }
      return false;
    }
    if (moment(date).isBefore(this.currentDate)) {
      const diffDays = moment(this.currentDate)
        .startOf("day")
        .diff(moment(date).startOf("day"), "days");
      if (diffDays === 1) {
        if (this.currentShiftDate == date) {
          if (shift == this.currentShiftData["usershiftId"]) return false;
          let currentFlag = false;
          return this.shiftsArr.some((val) => {
            if (val.usershiftId == this.currentShiftData["usershiftId"])
              currentFlag = true;
            if (val.usershiftId == shift) return !currentFlag;
          });
        }
        return true;
      }
      return true;
    } else if (moment(date).isSame(this.currentDate)) {
      if (this.currentShiftDate == date) {
        if (shift == this.currentShiftData["usershiftId"]) return false;
        let currentFlag = false;
        return this.shiftsArr.some((val) => {
          if (val.usershiftId == this.currentShiftData["usershiftId"])
            currentFlag = true;
          if (val.usershiftId == shift) return !currentFlag;
        });
      }
      return false;
    }
    return false;
  }

  handleSaveError(error: any) {
    const msg =
      error.message === "Server Unreachable"
        ? "Server Unreachable"
        : error.message;
    const type =
      error.message === "PartMapping Already Exists!" ? "warning" : "error";
    this.toastr[type](msg, type.toUpperCase());
  }

  getRandomColor() {
    this.color = "#" + Math.floor(Math.random() * 16777215).toString(16);
    return this.color;
  }

  isRowDisabled(index: number): boolean {
    return index < this.targetFormArray.length - 1;
  }

  close() {
    this.modalService.dismissAll();
    this.formData.reset();
  }

  clearTargetArray() {
    const targetArray = this.targetFormArray;
    while (targetArray.length) targetArray.removeAt(0);
  }

  confirm() {
    this.clearTargetArray();
    this.isReset = true;
  }

  close2() {
    this.isReset = false;
  }

  resetModal() {
    this.step = 1;
    this.changeOnlyOperator = null;
    this.configureProduction = null;
  }

  isCurrentPacket(rowValue: any): boolean {
    const now = moment();
    return (
      now.isSameOrAfter(moment(rowValue.startTime)) &&
      now.isBefore(moment(rowValue.endTime))
    );
  }

  // ==========================================
  // OPERATOR CHANGE FLOW
  // ==========================================

  onOperatorChoice(choice: boolean, modal: any) {
    this.changeOnlyOperator = choice;

    if (choice) {
      // YES — change only operator
      const row = this.targetFormArray.at(
        this.selectedOperatorIndex,
      ) as FormGroup;
      const rowValue = row.getRawValue();

      if (this.isCurrentPacket(rowValue)) {
        // Current packet: split into old + new operator packets
        this.applyOperatorSplit();
      } else {
        // Future packet: just patch operator directly
        row.patchValue({ operatorName: this.selectedOperatorName });
      }

      this.resetModal();
      modal.dismiss();
    } else {
      // NO — proceed to step 2
      this.step = 2;
    }
  }

  onProductionChoice(choice: boolean, modal: any, index: any) {
    this.configureProduction = choice;
    this.selectedOperatorIndex =
      this.selectedOperatorIndex != null ? this.selectedOperatorIndex : index;
    const row = this.targetFormArray.at(
      this.selectedOperatorIndex,
    ) as FormGroup;
    const rowValue = row.getRawValue();

    if (choice) {
      // YES — close current packet at NOW, remove subsequent packets, add new row
      const now = moment();
      const start = moment(rowValue.startTime);

      const cycleTime =
        Number(rowValue.runningTime || 0) +
        Number(rowValue.loadingTime || 0) +
        Number(rowValue.unloadingTime || 0);

      if (cycleTime > 0) {
        const availableSeconds = now.diff(start, "seconds");
        const capacity = Math.floor(availableSeconds / cycleTime);
        row.patchValue({
          operatorName: this.oldOperatorName,
          endTime: now.format("YYYY-MM-DD HH:mm:ss"),
          shiftCapacity: capacity,
          target: Math.min(rowValue.target, capacity),
        });
      }

      // Remove all rows after selected index
      this.removeItemsAfterSelected();

      this.resetModal();
      modal?.dismiss();

      // ✅ Add new row and pre-fill with selected operator
      setTimeout(() => {
        this.addPartwiseTarget("edit");
        const newIdx = this.targetFormArray.length - 1;
        if (newIdx >= 0) {
          (this.targetFormArray.at(newIdx) as FormGroup).patchValue({
            operatorName: this.selectedOperatorName,
          });
        }
      }, 50);
    } else {
      // NO — revert operator back to old value
      row.patchValue({ operatorName: this.oldOperatorName });
      this.resetModal();
      modal?.dismiss();
    }
  }

  applyOperatorSplit() {
    const now = moment().format("YYYY-MM-DD HH:mm:ss");
    const row = this.targetFormArray.at(
      this.selectedOperatorIndex,
    ) as FormGroup;
    const rowValue = row.getRawValue();

    const cycleTime =
      Number(rowValue.runningTime || 0) +
      Number(rowValue.loadingTime || 0) +
      Number(rowValue.unloadingTime || 0);

    // Close old packet at NOW
    const oldAvailableSeconds = moment(now).diff(
      moment(rowValue.startTime),
      "seconds",
    );
    const oldCapacity =
      cycleTime > 0 ? Math.floor(oldAvailableSeconds / cycleTime) : 0;
    const remainingTarget = Math.max(
      0,
      rowValue.target - Math.min(oldCapacity, rowValue.target),
    );

    row.patchValue({
      operatorName: this.oldOperatorName,
      endTime: now,
      shiftCapacity: oldCapacity,
      target: Math.min(oldCapacity, rowValue.target),
    });

    // Create new packet for new operator
    const newGroup = this.formBuilder.group({
      partName: rowValue.partName,
      operatorName: this.selectedOperatorName,
      operationName: rowValue.operationName,
      shiftCapacity: remainingTarget,
      runningTime: rowValue.runningTime,
      loadingTime: rowValue.loadingTime,
      unloadingTime: rowValue.unloadingTime,
      sequenceData: [rowValue?.sequenceData || []],
      target: remainingTarget,
      startTime: now,
      endTime: rowValue.endTime,
      partsPerCycle: rowValue.partsPerCycle,
      countingType: rowValue.countingType,
    });

    newGroup.disable();
    newGroup.get("operatorName")?.enable();

    this.targetFormArray.insert(this.selectedOperatorIndex + 1, newGroup);

    this.rebuildOperationMaps();
  }

  rebuildOperationMaps() {
    this.partOperationMap = [];
    this.targetFormArray.controls.forEach((ctrl, index) => {
      const value = (ctrl as FormGroup).getRawValue();
      this.mapOperationsForEdit(value, index);
    });
  }

  removeItemsAfterSelected() {
    const length = this.targetFormArray.length;
    for (let i = length - 1; i > this.selectedOperatorIndex; i--) {
      this.targetFormArray.removeAt(i);
    }
  }
}
